<template>
  <div class="nb-wrap" ref="wrapRef">
    <!-- Bell trigger -->
    <button class="nb-btn" @click="toggle" :aria-label="`Notifications (${unreadCount} unread)`">
      <font-awesome-icon icon="bell" />
      <span v-if="unreadCount > 0" class="nb-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </button>

    <!-- Dropdown panel -->
    <transition name="nb-fade">
      <div v-if="open" class="nb-panel">
        <div class="nb-panel-head">
          <p class="nb-panel-title">Notifications</p>
          <button v-if="unreadCount > 0" class="nb-mark-all" @click="markAllRead">
            Mark all read
          </button>
        </div>

        <div v-if="loading" class="nb-loading">
          <div v-for="n in 4" :key="n" class="nb-sk"></div>
        </div>

        <div v-else>
          <div v-if="!notifications.length" class="nb-empty">
            <font-awesome-icon icon="bell-slash" class="nb-empty-icon" />
            <p>No notifications yet</p>
          </div>

          <div v-else class="nb-list">
            <div
              v-for="n in notifications"
              :key="n.id"
              class="nb-item"
              :class="{ 'nb-item--unread': !n.read_at }"
              @click="markRead(n)"
            >
              <div class="nb-item-icon" :class="iconClass(n.data?.type)">
                <font-awesome-icon :icon="iconName(n.data?.type)" />
              </div>
              <div class="nb-item-body">
                <p class="nb-item-msg">{{ n.data?.message || 'Notification' }}</p>
                <p class="nb-item-time">{{ timeAgo(n.created_at) }}</p>
              </div>
              <div v-if="!n.read_at" class="nb-unread-dot"></div>
            </div>
          </div>

          <div v-if="hasMore" class="nb-panel-foot">
            <button class="nb-load-more" @click="loadMore" :disabled="loadingMore">
              {{ loadingMore ? 'Loading…' : 'Load more' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

const TYPE_META = {
  leave_request:     { icon: 'umbrella-beach', cls: 'icon--blue' },
  missing_clock_out: { icon: 'clock',          cls: 'icon--orange' },
  payroll_lock:      { icon: 'lock',           cls: 'icon--red' },
  shift_assignment:  { icon: 'user-clock',     cls: 'icon--green' },
  rota:              { icon: 'calendar-alt',   cls: 'icon--green' },
  default:           { icon: 'bell',           cls: 'icon--gray' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)   return 'Just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  return Math.floor(diff / 86400) + 'd ago';
}

export default {
  name: 'NotificationBell',
  data() {
    return {
      open: false,
      loading: false,
      loadingMore: false,
      notifications: [],
      unreadCount: 0,
      currentPage: 1,
      lastPage: 1,
      pollTimer: null,
    };
  },
  computed: {
    hasMore() { return this.currentPage < this.lastPage; },
  },
  mounted() {
    this.fetchCount();
    // Poll unread count every 60 seconds
    this.pollTimer = setInterval(this.fetchCount, 60000);
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    clearInterval(this.pollTimer);
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    async fetchCount() {
      try {
        const { data } = await axios.get('/hr/notifications/unread-count');
        this.unreadCount = data.data?.count ?? 0;
      } catch (err){
        console.log(err.response?.data?.message);
      }
    },
    async fetchNotifications(page = 1) {
      if (page === 1) { this.loading = true; this.notifications = []; }
      else { this.loadingMore = true; }
      try {
        const { data } = await axios.get('/hr/notifications', { params: { length: 15, page } });
        const resp = data.data?.notifications;
        this.notifications = page === 1
          ? (resp?.data || [])
          : [...this.notifications, ...(resp?.data || [])];
        this.currentPage = resp?.current_page || 1;
        this.lastPage    = resp?.last_page    || 1;
        this.unreadCount = data.data?.unread_count ?? this.unreadCount;
      } catch (err){
        console.log(err.response?.data?.message);
      }
      finally { this.loading = false; this.loadingMore = false; }
    },
    toggle() {
      this.open = !this.open;
      if (this.open && !this.notifications.length) {
        this.fetchNotifications(1);
      }
    },
    loadMore() { this.fetchNotifications(this.currentPage + 1); },
    async markRead(n) {
      if (n.read_at) return;
      try {
        await axios.put(`/hr/notifications/${n.id}/read`);
        n.read_at = new Date().toISOString();
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      } catch (err){
        console.log(err.response?.data?.message);
      }
    },
    async markAllRead() {
      try {
        await axios.put('/hr/notifications/read-all');
        this.notifications.forEach(n => { n.read_at = n.read_at || new Date().toISOString(); });
        this.unreadCount = 0;
      } catch (err){
        console.log(err.response?.data?.message);
      }
    },
    handleOutsideClick(e) {
      if (this.$refs.wrapRef && !this.$refs.wrapRef.contains(e.target)) {
        this.open = false;
      }
    },
    iconName(type) { return (TYPE_META[type] || TYPE_META.default).icon; },
    iconClass(type) { return (TYPE_META[type] || TYPE_META.default).cls; },
    timeAgo,
  },
};
</script>

<style scoped>
.nb-wrap { position:relative; display:inline-flex; }

.nb-btn { position:relative; background:none; border:none; cursor:pointer; color:#4a5e78; font-size:18px; padding:6px 8px; border-radius:8px; transition:background .15s,color .15s; }
.nb-btn:hover { background:#f0f4f8; color:#004AAD; }

.nb-badge { position:absolute; top:2px; right:2px; min-width:17px; height:17px; border-radius:99px; background:#dc2626; color:#fff; font-size:10px; font-weight:800; display:flex; align-items:center; justify-content:center; padding:0 4px; border:2px solid #fff; }

/* Panel */
.nb-panel { position:absolute; top:calc(100% + 8px); right:0; width:360px; max-height:480px; background:#fff; border:1px solid #e4eaf2; border-radius:14px; box-shadow:0 12px 40px rgba(0,0,0,.14); z-index:2000; display:flex; flex-direction:column; overflow:hidden; }

.nb-panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-bottom:1px solid #f0f4f8; flex-shrink:0; }
.nb-panel-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }
.nb-mark-all    { background:none; border:none; cursor:pointer; font-size:12px; color:#004AAD; font-weight:600; padding:0; }
.nb-mark-all:hover { text-decoration:underline; }

.nb-loading { padding:12px 16px; display:flex; flex-direction:column; gap:8px; }
.nb-sk      { height:48px; border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.nb-empty { display:flex; flex-direction:column; align-items:center; gap:8px; padding:40px 16px; color:#7a90b0; }
.nb-empty-icon { font-size:32px; opacity:.4; }
.nb-empty p { margin:0; font-size:13px; }

.nb-list { overflow-y:auto; max-height:360px; }

.nb-item { display:flex; align-items:flex-start; gap:12px; padding:12px 16px; cursor:pointer; transition:background .1s; border-bottom:1px solid #f8fafc; }
.nb-item:hover { background:#f8fafc; }
.nb-item--unread { background:#f0f7ff; }
.nb-item--unread:hover { background:#e0efff; }

.nb-item-icon { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
.icon--blue   { background:#dbeafe; color:#1d4ed8; }
.icon--orange { background:#ffedd5; color:#ea580c; }
.icon--red    { background:#fee2e2; color:#dc2626; }
.icon--green  { background:#dcfce7; color:#16a34a; }
.icon--gray   { background:#f1f5f9; color:#64748b; }

.nb-item-body { flex:1; min-width:0; }
.nb-item-msg  { font-size:12px; color:#1a2b45; margin:0 0 3px; line-height:1.4; }
.nb-item-time { font-size:11px; color:#7a90b0; margin:0; }

.nb-unread-dot { width:8px; height:8px; border-radius:50%; background:#004AAD; flex-shrink:0; margin-top:4px; }

.nb-panel-foot { padding:10px 16px; border-top:1px solid #f0f4f8; text-align:center; flex-shrink:0; }
.nb-load-more  { background:none; border:none; cursor:pointer; font-size:13px; color:#004AAD; font-weight:600; }
.nb-load-more:disabled { opacity:.5; cursor:not-allowed; }

/* Transition */
.nb-fade-enter-active,.nb-fade-leave-active { transition:opacity .15s,transform .15s; }
.nb-fade-enter-from,.nb-fade-leave-to { opacity:0; transform:translateY(-6px); }
</style>
